
import React, { useRef, useState } from 'react';
import { StepStage } from '../types';
import { ArrowRight, ArrowLeft, Upload, FileCheck, Download, MessageSquareWarning, RefreshCw, X, Paperclip, FastForward, RotateCcw, FileText, Wand2, Image as ImageIcon, Check, PenTool, LayoutTemplate } from 'lucide-react';

interface Props {
  stage: StepStage;
  isThinking: boolean;
  onNext: () => void;
  onPrevious: () => void;
  onFeedback: (feedback: string, file?: File) => void;
  onUpload?: (files?: File[], text?: string) => void;
  onGeneratePlan?: (baseFile: File, styleFile?: File, customPrompt?: string) => Promise<string | null>; 
  onDownloadReport?: () => void;
  onDownloadPdf?: () => void; // Kept in interface but unused in UI
  onReset?: () => void;
}

export const StepActions: React.FC<Props> = ({ 
  stage, 
  isThinking: isThinking, 
  onNext, 
  onPrevious,
  onFeedback,
  onUpload, 
  onGeneratePlan,
  onDownloadReport,
  onReset
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const feedbackFileRef = useRef<HTMLInputElement>(null);
  const basePlanRef = useRef<HTMLInputElement>(null);
  const styleRefRef = useRef<HTMLInputElement>(null);

  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [feedbackFileName, setFeedbackFileName] = useState<string | null>(null);
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedbackText, setFeedbackText] = useState('');
  const [designText, setDesignText] = useState('');

  // AI Gen Modal States
  const [showGenModal, setShowGenModal] = useState(false);
  const [basePlanFile, setBasePlanFile] = useState<File | null>(null);
  const [styleRefFile, setStyleRefFile] = useState<File | null>(null);
  const [customGenPrompt, setCustomGenPrompt] = useState(''); 
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedImages, setGeneratedImages] = useState<string[]>([]);
  const [selectedImageIndex, setSelectedImageIndex] = useState<number | null>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setSelectedFiles(Array.from(e.target.files));
    }
  };
  
  const handleRemoveFiles = (e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedFiles([]);
    if (fileInputRef.current) {
        fileInputRef.current.value = "";
    }
  };

  const handleFeedbackFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
        setFeedbackFileName(e.target.files[0].name);
    }
  };

  const handleDesignSubmit = () => {
    if (onUpload) {
        if (selectedFiles.length > 0) {
            onUpload(selectedFiles, designText);
        } else if (designText.trim()) {
            onUpload(undefined, designText);
        }
    }
  };

  const handleSkipDesign = () => {
    if (onUpload) {
      onUpload(undefined, "用户选择不上传户型图。请严格基于 Step 4 产品策略（户型配比、面积段）进行本步骤的定价验证与总收入测算。");
    }
  };

  const submitFeedback = () => {
    if (feedbackText.trim() || feedbackFileName) {
      const file = feedbackFileRef.current?.files?.[0];
      onFeedback(feedbackText, file);
      setFeedbackText('');
      setFeedbackFileName(null);
      if (feedbackFileRef.current) feedbackFileRef.current.value = '';
      setShowFeedback(false);
    }
  };

  // AI Gen Handlers
  const handleStartGeneration = async () => {
    if (!basePlanFile || !onGeneratePlan) return;
    setIsGenerating(true);
    setGeneratedImages([]);
    setSelectedImageIndex(null);
    
    try {
        // Generate 3 options sequentially (or parallel if allowed)
        // Parallelizing for speed
        const promises = [1, 2, 3].map(() => 
            onGeneratePlan(basePlanFile, styleRefFile || undefined, customGenPrompt)
        );
        
        const results = await Promise.all(promises);
        const validResults = results.filter((img): img is string => img !== null);
        
        if (validResults.length > 0) {
            setGeneratedImages(validResults);
        } else {
            alert("生成失败，请重试");
        }
    } catch (e) {
        console.error(e);
        alert("生成出错");
    } finally {
        setIsGenerating(false);
    }
  };

  const handleConfirmGenerated = () => {
      if (selectedImageIndex !== null && generatedImages[selectedImageIndex] && onUpload) {
          fetch(`data:image/png;base64,${generatedImages[selectedImageIndex]}`)
            .then(res => res.blob())
            .then(blob => {
                const file = new File([blob], `ai_generated_plan_option_${selectedImageIndex + 1}.png`, { type: "image/png" });
                onUpload([file], "这是AI基于我的要求生成的强排平面图（已选定方案），请基于此图进行下一步点评和测算。");
            });
      }
  };

  if (isThinking) {
    return (
      <div className="fixed bottom-0 left-0 right-0 p-4 md:p-6 bg-white/95 backdrop-blur-md border-t border-gray-200 flex justify-center z-50">
         <div className="flex items-center space-x-3 text-blue-600 font-semibold animate-pulse">
            <span className="text-xl">⚡</span>
            <span className="text-sm md:text-base">AI 正在深度推演中... (Gemini 3.0 Pro)</span>
         </div>
      </div>
    );
  }

  // Final Report Action
  if (stage === StepStage.FINAL_REPORT) {
    return (
      <div className="fixed bottom-0 left-0 right-0 p-3 md:p-6 bg-white border-t border-gray-200 flex justify-between items-center z-50 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.1)]">
        <button
          onClick={onPrevious}
          className="flex items-center px-3 py-2 md:px-6 md:py-3 text-slate-600 font-bold hover:bg-slate-100 rounded-lg transition text-xs md:text-base"
        >
          <ArrowLeft className="w-4 h-4 md:w-5 md:h-5 mr-1 md:mr-2" />
          上一步
        </button>
        
        <div className="flex items-center space-x-2 md:space-x-4 overflow-x-auto">
            {onReset && (
                <button
                  onClick={onReset}
                  className="flex items-center px-3 py-2 md:px-5 md:py-3 bg-white border border-slate-300 text-slate-700 rounded-full font-bold text-xs md:text-base hover:bg-slate-50 transition shadow-sm whitespace-nowrap"
                >
                  <RotateCcw className="w-3 h-3 md:w-4 md:h-4 mr-1 md:mr-2" />
                  重开
                </button>
            )}
            
            <button
              onClick={onDownloadReport}
              className="flex items-center px-3 py-2 md:px-5 md:py-3 bg-green-600 text-white rounded-full font-bold text-xs md:text-base hover:bg-green-700 transition shadow-xl transform hover:scale-105 whitespace-nowrap"
            >
              <Download className="w-3 h-3 md:w-5 md:h-5 mr-1 md:mr-2" />
              下载 HTML
            </button>
        </div>
      </div>
    );
  }

  // Upload/Generate Logic for Step 5.5
  if (stage === StepStage.DESIGN_BRIEF) { 
     return (
        <>
        <div className="fixed bottom-0 left-0 right-0 p-4 md:p-6 bg-white border-t border-gray-200 flex flex-col items-center justify-center z-50 space-y-4 shadow-xl">
          <p className="text-xs md:text-sm text-gray-500 mb-1 font-medium text-center">请选择一种方式生成或上传设计方案</p>
          
          <div className="w-full max-w-4xl space-y-4 md:space-y-6">
             <div className="flex flex-col md:flex-row justify-center items-center space-y-3 md:space-y-0 md:space-x-4">
                 <button
                   onClick={onPrevious}
                   className="absolute left-4 md:static flex items-center px-3 py-2 text-slate-600 font-bold hover:bg-slate-100 rounded-lg transition text-sm"
                 >
                   <ArrowLeft className="w-4 h-4 mr-1" />
                   上一步
                 </button>

                 {/* Option 1: AI Generate */}
                 <button
                    onClick={() => setShowGenModal(true)}
                    className="flex items-center px-5 py-3 bg-purple-600 text-white rounded-xl font-bold hover:bg-purple-700 transition shadow-md transform hover:scale-105 w-full md:w-auto justify-center"
                 >
                    <Wand2 className="w-5 h-5 mr-2" />
                    AI 自动强排 (3选1)
                 </button>

                 <div className="hidden md:block w-px h-10 bg-gray-300 mx-2"></div>

                 {/* Option 2: Manual Upload */}
                 <div className="flex items-center space-x-2 w-full md:w-auto">
                    <input
                        type="file"
                        multiple
                        ref={fileInputRef}
                        className="hidden"
                        accept="image/*,.xlsx,.xls,.csv,.pdf"
                        onChange={handleFileChange}
                    />
                    {selectedFiles.length > 0 ? (
                        <div className="flex items-center px-4 py-3 border-2 border-green-400 bg-green-50 text-green-700 rounded-xl font-bold transition text-sm w-full md:w-auto justify-center">
                            <FileCheck className="w-4 h-4 mr-2" />
                            已选: {selectedFiles.length}
                            <button onClick={handleRemoveFiles} className="ml-3 p-1 hover:bg-green-200 rounded-full"><X className="w-4 h-4" /></button>
                        </div>
                    ) : (
                        <button
                            onClick={() => fileInputRef.current?.click()}
                            className="flex items-center px-5 py-3 bg-white border border-gray-300 text-slate-700 rounded-xl font-bold hover:bg-slate-50 transition shadow-sm text-sm w-full md:w-auto justify-center"
                        >
                            <Upload className="w-5 h-5 mr-2 text-blue-600" />
                            上传已有方案
                        </button>
                    )}
                 </div>
             </div>

             {/* Text Input for Manual Description */}
             <div className="relative">
                <textarea
                    className="w-full border border-gray-300 rounded-lg p-2 md:p-3 text-xs md:text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    rows={2}
                    placeholder="如有上传户型图，请在此补充说明特点（如：层高限制、特殊隔断要求等）；或者直接输入文字方案..."
                    value={designText}
                    onChange={(e) => setDesignText(e.target.value)}
                />
             </div>

             {/* Proceed Button */}
             <div className="flex flex-col items-center justify-center space-y-3">
                 {(selectedFiles.length > 0 || designText.trim()) && (
                     <button
                       onClick={handleDesignSubmit}
                       className="flex items-center px-10 py-3 bg-blue-600 text-white rounded-full font-bold hover:bg-blue-700 transition shadow-lg transform hover:scale-105 text-base w-full md:w-auto justify-center"
                     >
                       <FileCheck className="w-5 h-5 mr-2" />
                       确认并下一步
                     </button>
                 )}
                 
                 {/* Styled Skip Button */}
                 {(!selectedFiles.length && !designText.trim()) && (
                    <button
                        onClick={handleSkipDesign}
                        className="flex items-center px-6 py-2 bg-slate-50 border border-slate-300 text-slate-600 rounded-full font-semibold hover:bg-slate-100 transition shadow-sm text-sm w-full md:w-auto justify-center"
                    >
                        <FastForward className="w-4 h-4 mr-2" />
                        跳过，使用 AI 默认策略
                    </button>
                 )}
             </div>
          </div>
        </div>
        {showGenModal && (
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[60] flex items-center justify-center p-4">
                <div className="bg-white rounded-2xl w-full max-w-5xl max-h-[90vh] overflow-y-auto shadow-2xl flex flex-col">
                    <div className="p-5 border-b border-gray-100 flex justify-between items-center bg-purple-50 rounded-t-2xl">
                        <h3 className="font-bold text-purple-900 flex items-center text-lg">
                            <LayoutTemplate className="w-5 h-5 mr-2 text-purple-600" />
                            AI 自动强排生成 (方案 3选1)
                        </h3>
                        <button onClick={() => setShowGenModal(false)} className="text-gray-400 hover:text-gray-600"><X className="w-6 h-6" /></button>
                    </div>
                    
                    <div className="p-6 space-y-6 flex-1">
                        {generatedImages.length === 0 ? (
                            <>
                                <p className="text-sm text-gray-700 bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                                    ⚠️ <strong>最佳效果提示：</strong>请务必上传【黑白 CAD 导出的线稿图】，图中黑色粗线应代表<strong>承重墙/剪力墙</strong>。
                                </p>
                                
                                <div className="space-y-4">
                                    {/* Upload Base Plan */}
                                    <div className="border-2 border-dashed border-gray-300 rounded-xl p-6 text-center hover:bg-gray-50 transition cursor-pointer" onClick={() => basePlanRef.current?.click()}>
                                        <input type="file" ref={basePlanRef} className="hidden" accept="image/*" onChange={(e) => e.target.files && setBasePlanFile(e.target.files[0])} />
                                        <div className="flex flex-col items-center">
                                            {basePlanFile ? (
                                                <>
                                                    <FileCheck className="w-10 h-10 text-green-500 mb-2" />
                                                    <span className="font-bold text-gray-800">{basePlanFile.name}</span>
                                                    <span className="text-xs text-gray-500">原始标准层结构图 (必填)</span>
                                                </>
                                            ) : (
                                                <>
                                                    <Upload className="w-10 h-10 text-gray-400 mb-2" />
                                                    <span className="font-bold text-gray-600">点击上传黑白结构底图 (必填)</span>
                                                    <span className="text-xs text-gray-400">支持 PNG/JPG 格式</span>
                                                </>
                                            )}
                                        </div>
                                    </div>

                                    {/* Upload Style Ref */}
                                    <div className="border border-gray-200 rounded-xl p-4 flex items-center justify-between hover:bg-gray-50 transition cursor-pointer" onClick={() => styleRefRef.current?.click()}>
                                        <input type="file" ref={styleRefRef} className="hidden" accept="image/*" onChange={(e) => e.target.files && setStyleRefFile(e.target.files[0])} />
                                        <div className="flex items-center">
                                            <ImageIcon className="w-5 h-5 text-purple-500 mr-3" />
                                            <div className="text-left">
                                                <div className="font-medium text-gray-700 text-sm">上传风格参考图 (选填)</div>
                                                <div className="text-xs text-gray-500">{styleRefFile ? styleRefFile.name : "如日式原木、工业风照片..."}</div>
                                            </div>
                                        </div>
                                        {styleRefFile && <Check className="w-5 h-5 text-green-500" />}
                                    </div>

                                    {/* Custom Prompt Input */}
                                    <div className="border border-gray-200 rounded-xl p-4 flex flex-col hover:bg-gray-50 transition">
                                        <div className="flex items-center mb-2">
                                            <PenTool className="w-5 h-5 text-blue-500 mr-2" />
                                            <span className="font-medium text-gray-700 text-sm">额外提示词 (选填)</span>
                                        </div>
                                        <textarea 
                                            value={customGenPrompt}
                                            onChange={(e) => setCustomGenPrompt(e.target.value)}
                                            placeholder="例如：Loft户型请生成上下层；走廊宽度1.5米..."
                                            className="w-full text-sm p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white h-20"
                                        />
                                    </div>
                                </div>

                                <button 
                                    onClick={handleStartGeneration}
                                    disabled={!basePlanFile || isGenerating}
                                    className="w-full py-4 bg-purple-600 text-white rounded-xl font-bold hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition shadow-lg flex justify-center items-center"
                                >
                                    {isGenerating ? (
                                        <>
                                            <Wand2 className="w-5 h-5 mr-2 animate-spin" />
                                            正在生成 3 个设计方案 (约20秒)...
                                        </>
                                    ) : (
                                        "开始生成方案 (3选1)"
                                    )}
                                </button>
                            </>
                        ) : (
                            <div className="flex flex-col items-center space-y-6">
                                <h4 className="text-gray-700 font-bold">请选择最满意的一个方案：</h4>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 w-full">
                                    {generatedImages.map((img, idx) => (
                                        <div 
                                            key={idx}
                                            onClick={() => setSelectedImageIndex(idx)}
                                            className={`relative group cursor-pointer border-4 rounded-xl overflow-hidden transition-all transform hover:scale-[1.02] ${selectedImageIndex === idx ? 'border-green-500 ring-4 ring-green-100' : 'border-transparent hover:border-gray-300'}`}
                                        >
                                            <div className="aspect-square bg-gray-100 relative">
                                                <img src={`data:image/png;base64,${img}`} alt={`Option ${idx+1}`} className="w-full h-full object-contain" />
                                            </div>
                                            <div className="absolute top-2 left-2 bg-black/60 text-white px-2 py-1 rounded text-xs font-bold">
                                                方案 {idx + 1}
                                            </div>
                                            {selectedImageIndex === idx && (
                                                <div className="absolute top-2 right-2 bg-green-500 text-white p-1 rounded-full">
                                                    <Check className="w-4 h-4" />
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>

                                <div className="flex space-x-3 w-full max-w-md mx-auto">
                                    <button 
                                        onClick={() => { setGeneratedImages([]); setSelectedImageIndex(null); }}
                                        className="flex-1 py-3 border border-gray-300 text-gray-700 rounded-xl font-bold hover:bg-gray-50 transition"
                                    >
                                        重试 / 重新生成
                                    </button>
                                    <button 
                                        onClick={handleConfirmGenerated}
                                        disabled={selectedImageIndex === null}
                                        className="flex-1 py-3 bg-green-600 text-white rounded-xl font-bold hover:bg-green-700 transition shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        确认使用方案 {selectedImageIndex !== null ? selectedImageIndex + 1 : ''}
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        )}
        </>
     );
  }

  // Feedback Mode UI (unchanged)
  if (showFeedback) {
      return (
        <div className="fixed bottom-0 left-0 right-0 p-4 md:p-6 bg-white border-t border-gray-200 z-50 shadow-2xl animate-fade-in-up">
            <div className="max-w-5xl mx-auto">
                <div className="flex justify-between items-center mb-3">
                    <h3 className="font-bold text-slate-800 flex items-center text-sm md:text-base">
                        <MessageSquareWarning className="w-4 h-4 md:w-5 md:h-5 mr-2 text-orange-500"/>
                        纠正 AI / 补充信息
                    </h3>
                    <button onClick={() => setShowFeedback(false)} className="text-gray-400 hover:text-gray-600">
                        <X className="w-5 h-5" />
                    </button>
                </div>
                <textarea 
                    className="w-full border border-gray-300 rounded-lg p-3 focus:ring-2 focus:ring-blue-500 focus:border-transparent mb-3 text-sm"
                    rows={3}
                    placeholder="例如：位置识别错误，项目实际位于万科万致天地，请重新分析周边竞品..."
                    value={feedbackText}
                    onChange={(e) => setFeedbackText(e.target.value)}
                    autoFocus
                />
                
                {/* Feedback File Upload */}
                <div className="flex items-center mb-4 space-x-2">
                    <input
                        type="file"
                        ref={feedbackFileRef}
                        className="hidden"
                        accept="image/*,.xlsx,.xls,.csv"
                        onChange={handleFeedbackFileChange}
                    />
                    <button
                       onClick={() => feedbackFileRef.current?.click()}
                       className="flex items-center text-xs md:text-sm text-blue-600 hover:text-blue-800 font-medium"
                    >
                       <Paperclip className="w-3 h-3 md:w-4 md:h-4 mr-1" />
                       {feedbackFileName ? `附件: ${feedbackFileName}` : "上传佐证材料 (图片/Excel)"}
                    </button>
                </div>

                <div className="flex justify-end space-x-3 md:space-x-4">
                    <button 
                        onClick={() => setShowFeedback(false)}
                        className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg transition text-xs md:text-sm"
                    >
                        取消
                    </button>
                    <button 
                        onClick={submitFeedback}
                        className="px-4 py-2 bg-orange-600 text-white font-bold rounded-lg hover:bg-orange-700 transition flex items-center shadow-lg text-xs md:text-sm"
                    >
                        <RefreshCw className="w-3 h-3 md:w-4 md:h-4 mr-2" />
                        提交并重算
                    </button>
                </div>
            </div>
        </div>
      );
  }

  // Default Action Bar (unchanged)
  return (
    <div className="print:hidden fixed bottom-0 left-0 right-0 p-3 md:p-6 bg-white border-t border-gray-200 flex justify-between z-50 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.05)]">
      <div className="max-w-5xl w-full mx-auto flex justify-between items-center">
        
        {/* Left: Previous */}
        <button
          onClick={onPrevious}
          className="flex items-center px-3 py-2 md:px-6 md:py-3 text-slate-600 font-bold hover:bg-slate-100 rounded-lg transition disabled:opacity-50 text-xs md:text-base"
        >
          <ArrowLeft className="w-4 h-4 md:w-5 md:h-5 mr-1 md:mr-2" />
          <span className="hidden xs:inline">上一步</span>
        </button>

        {/* Right: Feedback & Next */}
        <div className="flex space-x-2 md:space-x-4">
            <button
                onClick={() => setShowFeedback(true)}
                className="flex items-center px-3 py-2 md:px-5 md:py-3 text-orange-700 bg-orange-50 border border-orange-200 hover:bg-orange-100 rounded-lg font-semibold transition text-xs md:text-base whitespace-nowrap"
            >
                <MessageSquareWarning className="w-4 h-4 md:w-5 md:h-5 mr-1 md:mr-2" />
                纠错
            </button>
            <button
                onClick={onNext}
                className="flex items-center px-4 py-2 md:px-8 md:py-3 bg-slate-900 text-white rounded-lg font-bold text-xs md:text-lg hover:bg-slate-800 transition shadow-lg whitespace-nowrap"
            >
                {stage === StepStage.FINANCIAL_MODEL ? "总结备忘" : "下一步"}
                <ArrowRight className="w-4 h-4 md:w-5 md:h-5 ml-1 md:ml-2" />
            </button>
        </div>
      </div>
    </div>
  );
};
